import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { HiOutlineGlobe } from 'react-icons/hi';
import Flex from 'legacyfrontend/components/Flex';
import { getGeoData } from 'legacyfrontend/redux/actions/application-actions';
import aplicaciones from 'legacyfrontend/api/aplicaciones';
import {
  selectApplicationId,
  selectGeoLocalities,
} from 'legacyfrontend/redux/selectors/application-selectors';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { useNavigation } from 'legacyfrontend/hooks/useNavigation';
import AgencyCardContent from '../agencies/AgencyCardContent';
import ContactFormModal from '../ContactFormModal';
import '../../../styles/components/map-agencies.css';

export default function MapAgencies() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { goBack } = useNavigation();
  const { background, text } = useColors();

  const applicationId = useSelector(selectApplicationId);
  const geoLocalities = useSelector(selectGeoLocalities);

  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState();

  const getAgencies = useCallback(async () => {
    try {
      const { data: agencies } = await aplicaciones.getOffices(applicationId);
      setAgencies(agencies);
    } catch (error) {
      console.error('Error fetching agencies:', error);
      setAgencies([]);
    }
  }, [applicationId]);

  const getAgencyLocationData = useCallback(
    (agency) => {
      if (!geoLocalities?.data) return null;

      return geoLocalities.data.find((locality) => locality.id === agency.localidadId);
    },
    [geoLocalities]
  );

  const createGoogleMapUrl = useCallback((lat, lng) => {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }, []);

  const handleAgencyModalOpen = useCallback((agency) => {
    setSelectedAgency(agency);
  }, []);

  useEffect(() => {
    if (applicationId) {
      getAgencies();
    }
    dispatch(getGeoData());
  }, [applicationId, getAgencies, dispatch]);

  // Memoize the agencies list to prevent unnecessary re-renders
  const agencyList = useMemo(() => {
    return agencies.map((agency) => {
      const location = getAgencyLocationData(agency);
      return (
        <Grid item key={agency.id} md={4} sm={6} xs={12}>
          <Card className='agencies__grid__card'>
            <CardContent>
              <AgencyCardContent agency={agency} location={location} />
            </CardContent>
            <CardActions>
              <Flex className='agencies__grid__card__row align-to-end'>
                <a
                  href={createGoogleMapUrl(agency.latitud, agency.longitud)}
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  <button className='agencies__grid__card__map_button'>
                    <HiOutlineGlobe size={24} />
                    <Typography>{t('translations:see_map')}</Typography>
                  </button>
                </a>
                <button
                  className='agencies__grid__card__contact_button'
                  onClick={() => handleAgencyModalOpen(agency)}
                  style={{
                    backgroundColor: background,
                    color: text,
                  }}
                >
                  <Typography style={{ color: text }}>{t('translations:contact')}</Typography>
                </button>
              </Flex>
            </CardActions>
          </Card>
        </Grid>
      );
    });
  }, [
    agencies,
    getAgencyLocationData,
    createGoogleMapUrl,
    handleAgencyModalOpen,
    t,
    background,
    text,
  ]);

  return (
    <Flex className='map-agencies-container p-4 gap-8' vertical>
      <div className='map-agencies-header'>
        <button className='map-agencies-back-button' onClick={goBack} aria-label='Volver atrás'>
          <i className='fa fa-chevron-left text-gray-600' />
        </button>
        <h3 className='map-agencies-title'>{t('translations:offices')}</h3>
        <div style={{ width: '40px' }}></div> {/* Spacer for centering */}
      </div>
      <Grid container spacing={2}>
        {agencyList}
      </Grid>
      <ContactFormModal
        consultaPackage={selectedAgency}
        onClose={() => setSelectedAgency(null)}
        variant='agency'
        title='Enviá tu consulta'
        subtitle={selectedAgency?.nombre}
      />
    </Flex>
  );
}
